import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Brand } from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, refreshUsage } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | timeout | error
  const sessionId = params.get("session_id");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setStatus("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid" || data.status === "completed") {
        await refreshUser();
        await refreshUsage();
        setStatus("success");
        return;
      }
      if (["failed", "expired"].includes(data.status)) { setStatus("error"); return; }
    } catch { /* keep polling */ }
    if (attempt >= MAX_POLLS) { setStatus("timeout"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId, refreshUser, refreshUsage]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#15151d] border border-[#2a3040] rounded-2xl p-10 text-center" data-testid="payment-success-page">
        <div className="mb-6"><Logo size="text-3xl" /></div>
        {status === "checking" && (
          <>
            <Loader2 size={48} className="text-[#FF5A00] mx-auto mb-4 animate-spin" />
            <h1 className="font-display font-black text-2xl mb-2">Confirming your payment…</h1>
            <p className="text-[#9ca3af] text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 size={56} className="text-[#4ade80] mx-auto mb-4" />
            <h1 className="font-display font-black text-3xl mb-2">Welcome to Pro! 🔥</h1>
            <p className="text-[#9ca3af] text-sm mb-6">Unlimited chats, longer responses, image generation, and the resume builder are now unlocked.</p>
            <button data-testid="success-continue-button" onClick={() => navigate("/")}
              className="w-full bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3 hover:bg-[#FF7A33] transition-colors">
              Start using Pro
            </button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <XCircle size={56} className="text-[#facc15] mx-auto mb-4" />
            <h1 className="font-display font-black text-2xl mb-2">
              {status === "timeout" ? "Still processing…" : "Something went wrong"}
            </h1>
            <p className="text-[#9ca3af] text-sm mb-6">
              {status === "timeout"
                ? "Your payment may take a minute to confirm. Refresh shortly."
                : "We couldn't confirm this payment. If you were charged, it will reflect soon."}
            </p>
            <button onClick={() => navigate("/")}
              className="w-full bg-[#15151d] border border-[#2a3040] text-white font-bold rounded-lg px-6 py-3 hover:bg-[#1a1a24] transition-colors">
              Back to <Brand />
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#15151d] border border-[#2a3040] rounded-2xl p-10 text-center" data-testid="payment-cancel-page">
        <div className="mb-6"><Logo size="text-3xl" /></div>
        <XCircle size={56} className="text-[#9ca3af] mx-auto mb-4" />
        <h1 className="font-display font-black text-2xl mb-2">Checkout cancelled</h1>
        <p className="text-[#9ca3af] text-sm mb-6">No worries — you can upgrade to Pro anytime.</p>
        <button data-testid="cancel-back-button" onClick={() => navigate("/")}
          className="w-full bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3 hover:bg-[#FF7A33] transition-colors">
          Back to <Brand />
        </button>
      </motion.div>
    </div>
  );
}
